<template>
  <div class="products-by-tag" />
</template>

<script>

/**
 * Vue: Products by tag (products-by-tag)
 * -----------------------------------------------------------------------------
 * Renderless component that grabs products by tag data for use in size selector
 * and colour swatches.
 *
 */
import { mapActions, mapState } from 'vuex'

import products from '~/graphql/queries/products.gql'

import { formatProduct } from '~/helpers/format'


export default {
  name: 'ProductsByTag',

  props: {
    namespace: {
      type: String,
      default: 'product',
    },
    product: {
      type: [Object, Boolean],
      default: false,
    },
  },

  emits: ['products-by-tag', 'products-by-tag-loaded'],

  data() {
    return {
      response: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      colourFamily: (state) => state.product.colourFamily,
      sizeFamily: (state) => state.product.sizeFamily,
      productsByTag: (state) => state.product.productsByTag,
    }),
  },

  watch: {

    /**
     * When the product updates re-init the component.
     */
    product: {
      eager: true,
      handler: function() {
        this.init()
      },
    },

    /**
     * When the tag updates, refresh the products by tag array.
     */
    tag(val) {
      if (!val) {
        return
      }
      this.getProductsByTag(val)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setCurrentSize: 'product/setCurrentSize',
      setSizeFamily: 'product/setSizeFamily',
      setColourFamily: 'product/setColourFamily',
      setProductsByTag: 'product/setProductsByTag',
      setQuickAddProductsByTag: 'product/setQuickAddProductsByTag',
      setProductsByTagState: 'product/setProductsByTagState',
    }),

    /**
     * Main init function to kick off functionality.
     */
    async init() {
      try{
        await this.getTagSize()
        await this.getSizeFamilyTag()
        await this.getColourFamilyTag()

        const tag = (this.colourFamily || this.sizeFamily) ?? null
        await this.getProductsByTag(tag)
      }catch(err) {
        console.log({ err })
      }
    },

    /**
     * Returns the colour family tag from the product tags.
     * @returns {String}
     */
    getColourFamilyTag() {
      return new Promise((resolve) => {
        if (!this.product) {
          resolve()
        }

        const colourTag = this.product.tags.find(
          tag => tag.includes('ProductColourFamily-'),
        )

        if (!colourTag) {
          resolve()
        }

        this.setColourFamily(colourTag)
        resolve()
      })
    },

    /**
     * Returns the size family from the product tags.
     * @returns {String}
     */
    getSizeFamilyTag() {
      return new Promise((resolve) => {
        if (!this.product) {
          resolve()
        }

        const sizeTag = this.product.tags.find(
          tag => tag.includes('ProductSizeFamily-'),
        )

        if (!sizeTag) {
          resolve()
        }

        this.setSizeFamily(sizeTag)
        resolve()
      })
    },

    /**
     * Returns the size from the product tags.
     * @returns {String}
     */
    getTagSize() {
      return new Promise((resolve) => {
        if (!this.product?.tags) {
          resolve()
        }

        const size = this.product.tags.find(tag => tag.includes('ProductSize-'))

        if (!size) {
          resolve()
        }

        const sizeText = size.split('-')[1] || false
        this.setCurrentSize(sizeText)
        resolve()
      })
    },

    /**
     * Loads linked size product from GraphQL.
     */
    async getProductsByTag(tag) {
      if (!tag) {
        this.setProductsByTagState('ready')
        return
      }

      if (!this.productsByTag?.length){
        this.setProductsByTagState('loading')
      }

      const tagString = `tag:${tag}`

      try {
        const response = await cnvs.Query({
          query: products,
          variables: {
            country: cnvs.store.country,
            query: tagString,
            language: cnvs.store.language,
          },
        })

        if (!response?.products?.edges?.length) {
          this.$emit('products-by-tag', null)
          this.setProductsByTagState('ready')
          return
        }

        const formattedResponse =
          response.products.edges.map(edge => formatProduct({ product: edge.node }))

        this.response = formattedResponse

        if (this.namespace === 'product') {
          this.setProductsByTag(formattedResponse)
        } else {
          this.setQuickAddProductsByTag(formattedResponse)
        }

        this.$emit('products-by-tag', formattedResponse)
      } catch (error) {
        reportError('Failed to load product', error)
        this.$emit('products-by-tag', null)
      } finally {
        this.setProductsByTagState('ready')
      }
    },
  },
}
</script>
